import { useAuth, useUser } from '@clerk/clerk-react';
import axios from 'axios';
import { useQuery } from 'react-query';
import { queryKeys } from '../network/queryKeys';
import { getRoleLevel, RoleLevel } from '../utils/roles';

const API_HOST = process.env.REACT_APP_API_HOST;

async function fetchCurrentUserProfile() {
  const { data } = await axios.get(`${API_HOST}/v1/profile`);
  return data;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/** The stored `system.role` on the signed-in user's profile, if sent. */
function roleFromProfile(profile: any): string | undefined {
  return (
    nonEmptyString(profile?.role) ??
    nonEmptyString(profile?.system?.role) ??
    nonEmptyString(profile?.data?.role)
  );
}

export type CurrentUserRole = {
  /** The raw role that decided `level`, if any source had one. */
  role?: string;
  level: RoleLevel;
  isLoading: boolean;
};

/**
 * The signed-in user's role, for hiding and guarding screens. The API's
 * checks are the real enforcement; this only keeps people out of screens
 * they cannot use.
 *
 * The stored role from GET /v1/profile wins, as that is what the API
 * checks. Until the profile carries it, fall back to the role Clerk holds
 * (public metadata, then the organisation role such as `org:admin`).
 */
export function useCurrentUserRole(): CurrentUserRole {
  const { isLoaded, orgRole } = useAuth();
  const { user } = useUser();
  const { data: profile, isLoading } = useQuery(
    queryKeys.currentUserProfile,
    fetchCurrentUserProfile,
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const role =
    roleFromProfile(profile) ??
    nonEmptyString(user?.publicMetadata?.role) ??
    nonEmptyString(orgRole);

  return {
    role,
    level: getRoleLevel(role),
    isLoading: !isLoaded || isLoading,
  };
}
